import React, { useEffect, useRef } from 'react';

export type ToastVariant = 'info' | 'success' | 'warning' | 'danger';

export interface ToastProps {
  readonly id: string;
  readonly variant?: ToastVariant;
  readonly message: string;
  readonly onDismiss: (id: string) => void;
  readonly autoDismissMs?: number;
}

export function Toast({
  id,
  variant = 'info',
  message,
  onDismiss,
  autoDismissMs = 5000,
}: ToastProps): React.ReactElement {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (autoDismissMs > 0) {
      timer.current = setTimeout(() => onDismiss(id), autoDismissMs);
    }
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [id, autoDismissMs, onDismiss]);
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={`toast-${id}`}
      data-variant={variant}
      style={{
        padding: '12px 16px',
        background: 'var(--color-bg-elevated)',
        color: 'var(--color-text-primary)',
        border: `1px solid var(--color-${variant === 'info' ? 'border-default' : variant})`,
        borderRadius: 6,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        marginTop: 8,
        minWidth: 280,
      }}
    >
      <span>{message}</span>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => onDismiss(id)}
        style={{ marginLeft: 12, background: 'none', border: 'none', cursor: 'pointer' }}
      >
        ×
      </button>
    </div>
  );
}

export interface ToastRegionProps {
  readonly toasts: ReadonlyArray<Omit<ToastProps, 'onDismiss'>>;
  readonly onDismiss: (id: string) => void;
}

export function ToastRegion({ toasts, onDismiss }: ToastRegionProps): React.ReactElement {
  return (
    <div
      role="region"
      aria-label="Notifications"
      data-testid="toast-region"
      style={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
      }}
    >
      {toasts.map((t) => (
        <Toast key={t.id} {...t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}
